<template>
  <div id="posSales">

    <PageTitle :title="$t('pos.sales.number') + ' ' + (sale ? sale.id : '...')"
      :subtitle="$t('pos.sales.customer_information')"></PageTitle>

    <div class="container">

      <div class="flex">

        <!-- Information panel -->
        <Panel :showTitle="false">
          <div class="panel__title">{{ $t("pos.sales.customer_branch_information") }}</div>
          <div class="panel__description">{{ $t("pos.sales.customer_information") }}</div>

          <!-- Customer -->
          <div class="panel__section" v-if="hasAnyContactInfo || hasAnyDeliveryInfo">
            <div class="section__title">{{ $t("customer.title") }}</div>
            <div class="panel__cards">
              <div class="cards__container" v-if="hasAnyContactInfo">
                <div class="cards__card title">
                  <div class="card__title">{{ $t("pos.contact") }}</div>
                  <div class="card__icon">
                    <svgicon src="icon/pos_contact.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.first_name") }}</div>
                      <div class="row__value">{{ sale ? sale.contact.firstName : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.last_name") }}</div>
                      <div class="row__value">{{ sale ? sale.contact.lastName : "..." }}</div>
                    </div>
                    <div class="card__row" v-if="sale && sale.contact.email">
                      <div class="row__title">{{ $t("pos.email") }}</div>
                      <div class="row__value">{{ sale.contact.email }}</div>
                    </div>
                    <div class="card__row" v-if="sale && sale.contact.phone">
                      <div class="row__title">{{ $t("pos.phone_number") }}</div>
                      <div class="row__value">{{ sale.contact.phone }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cards__container" v-if="hasAnyDeliveryInfo">
                <div class="cards__card title">
                  <div class="card__title">{{ $t("pos.location") }}</div>
                  <div class="card__icon">
                    <svgicon src="icon/pos_location.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.full_address") }}</div>
                      <div class="row__value">{{ sale.deliveryData.fullAddress }}</div>
                    </div>
                    <div class="card__row" v-if="sale.deliveryData.app">
                      <div class="row__title">{{ $t("pos.suite") }}</div>
                      <div class="row__value">{{ sale.deliveryData.app }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.postal_code") }}</div>
                      <div class="row__value">{{ sale.deliveryData.zip }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.city") }}</div>
                      <div class="row__value">{{ sale.deliveryData.city }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.country") }}</div>
                      <div class="row__value">{{ sale.deliveryData.country }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Panel>

        <!-- Order panel -->
        <Panel :showTitle="false">
          <div class="panel__title">{{ $t("pos.order.order_information") }}</div>
          <div class="panel__description">{{ $t("pos.order.order_information_description") }}</div>

          <!-- Order -->
          <div class="panel__section">
            <div class="section__title">{{ $t("pos.order.order_information") }}</div>
            <div class="panel__cards">
              <div class="cards__container">
                <div class="cards__card title">
                  <div class="card__title">{{ $t("pos.order.order_details") }}</div>
                  <div class="card__icon">
                    <svgicon src="icon/pos_options.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.order_number") }}</div>
                      <div class="row__value">{{ sale? sale.id : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.creation") }}</div>
                      <div class="row__value">{{ sale? sale.createdAt : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.platform") }}</div>
                      <div class="row__value">{{ sale? $t("platform." + sale.service) : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.order_type") }}</div>
                      <div class="row__value">{{ sale? $t("pos_method." + sale.deliveryMethod) : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.invoices") }}</div>
                      <div class="row__value">{{ sale? sale.invoices.length : "..." }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cards__container">
                <div class="cards__card title">
                  <div class="card__title">{{
                    $t("pos.location_reception.location_reception_time")
                  }}</div>
                  <div class="card__icon">
                    <svgicon src="icon/pos_location_time.svg" :width="20" :height="20">
                    </svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.full_address") }}</div>
                      <div class="row__value">{{ company? company.address.address : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.postal_code") }}</div>
                      <div class="row__value">{{ company? company.address.zipCode : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.city") }}</div>
                      <div class="row__value">{{ company? company.address.city : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.reception_time") }}</div>
                      <div class="row__value">{{ sale?(sale.inAdvanceFor) : "..." }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Content information -->
          <div class="panel__section">
            <div class="section__title">{{ $t("pos.content_information") }}</div>
            <div class="panel__cards">
              <div class="cards__container">
                <!-- Items -->
                <div class="cards__card" :class="{ title: detailIndex === 0 }"
                  v-for="(detail, detailIndex) in formattedDetails">
                  <div class="card__title" v-if="detailIndex === 0">{{ $t("pos.items") }}</div>
                  <div class="card__icon grey">
                    <svgicon src="icon/pos_photo.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row big">
                      <div class="row__value">{{ detail.name }}</div>
                    </div>
                    <div class="card__row modifier" v-for="modifier in detail.modifiers">
                      <div class="row__title">{{ modifier.name }}</div>
                      <div class="row__value" v-if="modifier.unitPrice">
                        {{ $tc(modifier.unitPrice, currency) }}
                      </div>
                    </div>
                    <div class="card__row bottom">
                      <div class="row__title">
                        {{ $t("quantity") }} : <span class="big">{{ detail.quantity }}</span>
                      </div>
                      <div class="row__value">{{ $tc(getDetailTotal(detail), currency) }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cards__container">
                <div class="cards__card title" v-if="hasAnyTotalInfo">
                  <div class="card__title">{{ $t("sales.supplements") }}</div>
                  <div class="card__icon">
                    <svgicon src="icon/pos_invoice.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("subTotal") }}</div>
                      <div class="row__value">{{ $tc(subTotal, currency) }}</div>
                    </div>
                    <div class="card__row modifier" v-for="tax in taxes">
                      <div class="row__title">{{ tax.name }}</div>
                      <div class="row__value">{{ $tc(tax.total, currency) }}</div>
                    </div>
                    <div class="card__row bottom">
                      <div class="row__title">{{ $t("total") }}</div>
                      <div class="row__value">{{ $tc(total, currency) }}</div>
                    </div>
                  </div>
                </div>
                <div class="cards__card title" v-for="payment in payments">
                  <div class="card__title">{{ $t("sales.payments") }}</div>
                  <div class="card__icon">
                    <svgicon src="icon/pos_payment.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("sales.payment_type") }}</div>
                      <div class="row__value">{{ $t("payment_method." + payment.method) }}</div>
                    </div>
                    <div class="card__row bottom">
                      <div class="row__title">{{ $t("sales.amount") }}</div>
                      <div class="row__value">{{ $tc(payment.amount, currency) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Panel>

        <!-- Invoices panel -->
        <Panel :showTitle="false" v-if="sale">
          <div class="panel__title">{{ $t("invoices") }}</div>
          <div class="panel__description">{{ $t("pos.consult_invoices") }}</div>
          <Datatable class="datatable" entity="invoice" :parameters="invoiceParameters" :maps="invoiceMappings" :onRowClick="onInvoiceRowClick" />
        </Panel>

        <!-- Invoice detail -->
        <Panel :showTitle="false" v-if="invoice">
          <div class="panel__title margin">{{ $t("invoice") }} {{ $t("pos.sales.number") }} {{
            invoice? invoice.number
          : "..." }}
          </div>

          <div class="panel__section">
            <div class="section__title">{{ $t("pos.content_information") }}</div>
            <div class="panel__cards">
              <div class="cards__container">
                <!-- Items -->
                <div class="cards__card" :class="{ title: detailIndex === 0 }"
                  v-for="(detail, detailIndex) in formattedDetails">
                  <div class="card__title" v-if="detailIndex === 0">{{ $t("pos.items") }}</div>
                  <div class="card__icon grey">
                    <svgicon src="icon/pos_photo.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row big">
                      <div class="row__value">{{ detail.name }}</div>
                    </div>
                    <div class="card__row modifier" v-for="modifier in detail.modifiers">
                      <div class="row__title">{{ modifier.name }}</div>
                      <div class="row__value" v-if="modifier.unitPrice">
                        {{ $tc(modifier.unitPrice, currency) }}
                      </div>
                    </div>
                    <div class="card__row bottom">
                      <div class="row__title">
                        {{ $t("quantity") }} : <span class="big">{{ detail.quantity }}</span>
                      </div>
                      <div class="row__value">{{ $tc(getDetailTotal(detail), currency) }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cards__container">
                <div class="cards__card title">
                  <div class="card__title">{{ $t("sales.supplements") }}</div>
                  <div class="card__icon">
                    <svgicon src="icon/pos_invoice.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("subTotal") }}</div>
                      <div class="row__value">{{ $tc(subTotal, currency) }}</div>
                    </div>
                    <div class="card__row modifier" v-for="tax in taxes">
                      <div class="row__title">{{ tax.name }}</div>
                      <div class="row__value">{{ $tc(tax.total, currency) }}</div>
                    </div>
                    <div class="card__row bottom">
                      <div class="row__title">{{ $t("total") }}</div>
                      <div class="row__value">{{ $tc(total, currency) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Payments -->
          <div class="panel__section noborder">
            <div class="section__title">{{ $t("items.payment_information") }}</div>
            <div class="panel__cards">
              <div class="cards__container">
                <div class="cards__card title" v-for="payment in payments">
                  <div class="card__title">{{ $t("sales.payments") }}</div>
                  <div class="card__icon">
                    <svgicon src="icon/pos_payment.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("sales.payment_type") }}</div>
                      <div class="row__value">{{ $t("payment_method." + payment.method) }}</div>
                    </div>
                    <div class="card__row bottom">
                      <div class="row__title">{{ $t("sales.amount") }}</div>
                      <div class="row__value">{{ $tc(payment.amount, currency) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </Panel>

        <Panel :showTitle="false">

          <!-- Branch -->
          <div class="panel__section">
            <div class="section__title">{{ $t("pos.selected_branch") }}</div>
            <div class="panel__cards">
              <div class="cards__container">
                <div class="cards__card title">
                  <div class="card__title">{{ $t("pos.contact") }}</div>
                  <div class="card__icon">
                    <svgicon src="icon/pos_branch.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row" v-if="$store.state.contextBranch">
                      <div class="row__title">{{ $t("pos.branch") }}</div>
                      <div class="row__value">{{ $store.state.contextBranch.name }}</div>
                    </div>
                    <div class="card__row" v-if="$store.state.contextCompany">
                      <div class="row__title">{{ $t("company_single") }}</div>
                      <div class="row__value">{{ $store.state.contextCompany.name }}</div>
                    </div>
                    <div class="card__row" v-if="company && company.email">
                      <div class="row__title">{{ $t("pos.email") }}</div>
                      <div class="row__value">{{ company.email }}</div>
                    </div>
                    <div class="card__row" v-if="company && company.phone">
                      <div class="row__title">{{ $t("pos.phone_number") }}</div>
                      <div class="row__value">{{ company.phoneNumber }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cards__container">
                <div class="cards__card title">
                  <div class="card__title">{{ $t("pos.location") }}</div>
                  <div class="card__icon">
                    <svgicon src="icon/pos_branch_marker.svg" :width="20" :height="20">
                    </svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.full_address") }}</div>
                      <div class="row__value">{{ company? company.address.address : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.postal_code") }}</div>
                      <div class="row__value">{{ company? company.address.zipCode : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.city") }}</div>
                      <div class="row__value">{{ company? company.address.city : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.province_state") }}</div>
                      <div class="row__value">{{ company? company.address.state : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.country") }}</div>
                      <div class="row__value">{{ company? company.address.country : "..." }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Advanced -->
          <div class="panel__section">
            <div class="section__title">{{ $t("pos.admin") }}</div>
            <div class="panel__cards">
              <div class="cards__container">
                <div class="cards__card">
                  <div class="card__icon">
                    <svgicon src="icon/pos_admin.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.ip") }}</div>
                      <div class="row__value">{{ sale? sale.saleData.ip : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.os") }}</div>
                      <div class="row__value">{{ sale? sale.saleData.os : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.app_name") }}</div>
                      <div class="row__value">{{ sale? sale.saleData.appName : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.app_version") }}</div>
                      <div class="row__value">{{ sale? sale.saleData.appVersion : "..." }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cards__container">
                <div class="cards__card">
                  <div class="card__icon">
                    <svgicon src="icon/pos_admin.svg" :width="20" :height="20"></svgicon>
                  </div>
                  <div class="card__content">
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.timezone") }}</div>
                      <div class="row__value">{{ sale? sale.saleData.timezone : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.language") }}</div>
                      <div class="row__value">{{ sale? sale.language : "..." }}</div>
                    </div>
                    <div class="card__row">
                      <div class="row__title">{{ $t("pos.user_agent") }}</div>
                      <div class="row__value">{{ sale? sale.saleData.userAgent : "..." }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </Panel>

        <PageFooter :backAction="{ action: backToSales, text: $t('pos.back_to_invoices') }">
        </PageFooter>

      </div>
    </div>

  </div>
</template>

<script>
  import Datatable from "@/components/Datatable.vue";
  import { mapActions } from "vuex";
  import PageFooter from "@/components/PageFooter.vue";
  import PageTitle from "@/components/PageTitle.vue";

  export default {
    components: { Datatable, PageTitle, PageFooter },
    data() {
      return {
        entity: "sale",
        parameters: {
          company: this.$route.query.company,
          service: "pos"
        },
        sale: null,
        company: null,
        invoice: null
      };
    },
    async mounted() {
      this.sale = await this.getSale({ saleId: this.$route.params.id, dto: "pos" });
      this.company = await this.getCompany({ companyId: this.$route.query.company, branchId: this.$route.query.branch });
    },
    computed: {
      payments() {
        return this.sale ? this.sale.payments : [];
      },
      currency() {
        return this.sale ? this.sale.currency : null;
      },
      subTotal() {
        return this.sale ? this.sale.subTotal : 0;
      },
      taxes() {
        return this.sale ? this.sale.taxes : [];
      },
      total() {
        return this.sale ? this.sale.total : 0;
      },
      details() {
        return this.sale ? this.sale.details : [];
      },
      company() {
        return this.$route.query.company || undefined;
      },
      formattedDetails() {
        let details = [];
        for (let detail of this.details) {
          if (detail.parentDetail === null) {
            details.push(detail);
          } else {
            let lastDetail = details[details.length - 1];
            if (!lastDetail.modifiers) {
              lastDetail.modifiers = [];
            }
            lastDetail.modifiers.push(detail);
          }
        }
        return details;
      },
      invoiceParameters() {
        return {
          company: this.$route.query.company,
          service: "pos",
          sale: this.sale.id
        };
      },
      invoiceMappings() {
        return [
          null,
          null,
          d => this.$t("platform." + d),
          d => this.$t("pos_method." + d),
          null,
          d => d ? this.$t("sale_status." + d) : d
        ];
      },
      hasAnyContactInfo() {
        if (!this.sale || !this.sale.contact) {
          return false;
        }
        return this.sale.contact.firstName || this.sale.contact.lastName || this.sale.contact.email || this.sale.contact.phone;
      },
      hasAnyDeliveryInfo() {
        if (!this.sale || !this.sale.deliveryData) {
          return false;
        }
        return this.sale.deliveryData.fullAddress ||
          this.sale.deliveryData.app ||
          this.sale.deliveryData.city ||
          this.sale.deliveryData.country ||
          this.sale.deliveryData.postalCode;
      },
      hasAnyTotalInfo() {
        if (!this.sale) {
          return false;
        }
        return this.sale.subTotal || this.sale.taxes.length > 0 || this.sale.total;
      }
    },
    methods: {
      ...mapActions(["getSale", "getCompany", "getInvoice"]),
      async onInvoiceRowClick(row) {
        this.invoice = await this.getInvoice({
          invoiceNumber: row.invoiceNumber,
          company: this.company.id
        });
      },
      getDetailTotal(detail) {
        let total = detail.unitPrice * detail.quantity;
        if (detail.modifiers) {
          for (let modifier of detail.modifiers) {
            total += modifier.unitPrice * modifier.quantity;
          }
        }
        return total;
      },
      backToSales() {
        this.$router.push(this.$router.buildContext("/pos-sales"));
      }
    }
  }
</script>

<style lang="scss" scoped>
  #posSales {
    display: flex;
    flex-direction: column;

    .datatable {
      flex-grow: 1;
    }
  }

  .container {
    display: flex;
    flex-direction: column;
  }

  .flex {
    display: flex;
    flex-grow: 1;
    overflow: hidden;
    flex-direction: column;
  }

  .panel {
    .panel__title {
      font-size: 14px;
      font-weight: bold;

      &.margin {
        margin-bottom: 24px;
      }
    }

    .panel__description {
      font-size: 12px;
      color: #808080;
      margin-bottom: 24px;
    }

    .datatable {
      flex-grow: 1;
      overflow: hidden;
    }
  }

  .panel__section {
    &:not(:last-child) {
      margin-bottom: 24px;
      padding-bottom: 24px;
      border-bottom: 1px dashed #aaa;
    }

    &.noborder {
      border-bottom: none;
    }

    .section__title {
      color: #FF5500;
      font-weight: bold;
      font-size: 12px;
      margin-bottom: 4px;
    }
  }

  .panel__cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 32px;

    .cards__container {
      &> :not(:last-child) {
        margin-bottom: 8px;
      }
    }

    .cards__card {
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      padding: 10px;
      display: flex;
      position: relative;

      &.title {
        margin-top: 36px;
      }

      .card__title {
        font-size: 12px;
        color: #808080;
        position: absolute;
        top: -16px;
        left: 0;
      }

      .card__icon {
        width: 40px;
        height: 40px;
        background-color: #ED2937;
        border-radius: 6px;
        fill: white;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 12px;
        flex-shrink: 0;

        &.grey {
          background-color: #F5F5F5;
          fill: #DBDBDB;
        }
      }

      .card__content {
        flex-grow: 1;
      }

      .card__row {
        display: flex;
        align-items: center;

        &.big {
          .row__value {
            font-size: 16px;
          }
        }

        &.modifier:not(:last-child) {
          border-bottom: none;
          margin-bottom: 0;

          .row__value {
            font-size: 12px;
            font-weight: bold;
            color: #808080;
          }

          &+.card__row.bottom {
            border-top: 1px solid #ddd;
            padding-top: 8px;
          }
        }

        &.bottom {
          .row__title .big {
            font-size: 16px;
            color: black;
          }

          .row__value {
            font-weight: bold;
          }
        }

        &:not(:last-child) {
          padding-bottom: 8px;
          margin-bottom: 8px;
          border-bottom: 1px solid #ddd;
        }

        .row__title {
          flex-grow: 1;
          font-size: 12px;
          color: #808080;
          margin-right: 10px;
          white-space: nowrap;
        }

        .row__value {
          font-size: 16px;
          text-align: right;

          .completed {
            color: #36D53D;
            display: flex;
            align-items: center;

            &:before {
              content: "";
              background-color: #36D53D;
              width: 8px;
              height: 8px;
              display: inline-block;
              border-radius: 50%;
              margin-right: 8px;
            }
          }
        }
      }
    }
  }
</style>

<style lang="scss">
  #posSales .panel .content-inner {
    overflow: hidden;
  }
</style>